import { BaseCategory } from "./BaseCategory";
import { FormatType, HeaderPositionType, HorizontalAlignType, PaletteColorType } from "../constants";
import { SlideCategories } from "./CategoryController";
import { SlideModelBuilder } from "./SlideModelBuilder";
import { v4 as uuid } from "uuid";

export class DashboardCategory extends BaseCategory {
    get name() {
        return "Dashboard";
    }

    get type() {
        return SlideCategories.DASHBOARD;
    }

    get icon() {
        return null;
    }

    get description() {
        return "Use for presenting statistics, factoids, data visualizations and metrics together, combining charts, statistics, radial bars, and text in a grid layout. Don't exceed 8 cells.";
    }

    get slideTypes() {
        return [];
    }

    getPrompt() {
        return `Create a dashboard slide based on the user's prompt. Only create cells for the information provided in user prompt as part of a dashboard.
         Don't add extra cells for title or conclusion. Do not invent additional cells beyond what the user prompt provides.
        Provide 1 to 8 cells with different component types: Chart, Statistic, RadialBar, or Text.
        If the user prompt does not provide actual data, generate realistic sample data for each cell.
        `;
    }

    getPromptSchema() {
        return {
            cells: [
                {
                    componentType: "Chart | Statistic | RadialBar | Factoid",
                    cellTitle: "optional title/label for the cell",
                    // For Chart components:
                    chartType: "line | bar | area (only for Chart components)",
                    categories: ["array of x-axis labels (only for Chart components)"],
                    series: [
                        {
                            name: "series name",
                            data: ["array of numeric values"]
                        }
                    ],
                    // For Statistic components:
                    text: "the statistic value including any notation symbols or qualifiers (like $, % or >) (only for Statistic components)",
                    statisticLabel: "optional trailing label for the statistic (only for Statistic components) ie 50% 'increase' OR +25% 'since last month' OR 400 'users'. Leave blank unless specified in user prompt.",
                    // For RadialBar components:
                    label: "label for the radial bar (only for RadialBar components)",
                    value: "numeric value between 0-100 (only for RadialBar components)",
                    // For Factoid components:
                    factoid: "richtext content (only for Factoid components)"
                }
            ],
            conclusion: "use if the prompt contains a conclusion or takeaway for the slide",
        };
    }

    getImagePrompt() {
        return false;
    }

    getSlideModels(model, options = {}) {
        let models = [
            this.buildDashboardSlide(model),
        ];

        return models;
    }

    buildDashboardSlide(model) {
        let builder = new SlideModelBuilder("dashboard", model);
        builder.setBackgroundColor(PaletteColorType.BACKGROUND_LIGHT);
        builder.createHeader({ heading: model.slideTitle, position: HeaderPositionType.TOP });

        const cellCount = Math.min(Math.max(model.cellCount || model.cells?.length || 1, 1), 8);
        const cells = model.cells || [];

        // Get appropriate grid layout for cell count
        const layout = this.getDashboardLayout(cellCount);

        // Build items for each cell
        const items = cells.map((cell, index) => {
            return this.buildCellItem(cell, builder);
        });

        builder.createPrimary({
            fullBleed: false,
            showGutter: true,
            cellColor: PaletteColorType.THEME,
            gridLayout: layout,
            items: items
        });

        if (model.conclusion) {
            builder.createBottomTray(model.conclusion);
        }

        return builder.model;
    }

    buildCellItem(cell, builder) {
        const componentType = cell.componentType || "Statistic";

        // Build the base cell model with common properties
        const cellModel = {
            componentType: componentType === "Factoid" ? "TextAndImage" : componentType,
            cellColor: PaletteColorType.BACKGROUND_ACCENT,
        };

        // Add title if provided
        if (cell.cellTitle) {
            cellModel.showTitle = "above";
            cellModel.chartTitle = {
                chartTitle: builder.createSingleBlockText(cell.cellTitle, "_use_styles_"),
                textAlign: HorizontalAlignType.LEFT
            };
        }

        // Build the childElement based on component type
        let childElement;
        switch (componentType) {
            case "Chart":
                childElement = this.buildChartChildElement(cell);
                break;
            case "Statistic":
                childElement = this.buildStatisticChildElement(cell, builder);
                break;
            case "RadialBar":
                childElement = this.buildRadialBarChildElement(cell, builder);
                break;
            case "Factoid":
                childElement = this.buildFactoidChildElement(cell, builder);
                break;
            default:
                childElement = this.buildStatisticChildElement(cell, builder);
        }

        cellModel.childElement = childElement;
        return cellModel;
    }

    buildChartChildElement(cell) {
        const chartType = cell.chartType || "line";
        const categories = cell.categories || ["Jan", "Feb", "Mar", "Apr", "May", "Jun"];
        const series = cell.series || [
            {
                name: "Data",
                data: [10, 40, 50, 30, 70, 50]
            }
        ];

        // Build series data with proper structure
        const chartSeries = series.map((s, index) => {
            const seriesIndex = index + 1;
            return {
                id: `series${seriesIndex}`,
                name: s.name || `Series ${seriesIndex}`,
                type: chartType,
                data: s.data.map(y => ({ y: y, pt: true })),
                colorName: `chart${seriesIndex}`,
                marker: "none",
                zones: [
                    {
                        style: "default",
                    }
                ]
            };
        });

        const chartData = {
            series: chartSeries,
            xAxis: {
                visible: true,
                showFirstLabel: true,
                showLastLabel: true,
                endOnTick: true,
                categories: categories,
                dateFormatting: "none",
                showAxisLine: true,
                showGridLine: false,
                showMajorTicks: false,
                showMinorTicks: false,
                axisTitle: "none",
                axisTitleText: "Title",
                labelInterval: 1,
                type: "linear",
                autoType: "linear",
                categoryType: "auto",
                labels: {
                    enabled: true,
                },
                zeroAxisPadding: false
            },
            yAxis: {
                visible: true,
                showFirstLabel: true,
                showLastLabel: true,
                endOnTick: true,
                showAxisLine: true,
                showGridLines: true,
                showMajorTicks: false,
                showMinorTicks: false,
                labelAbbreviation: false,
                labelCurrency: "none",
                labelFormat: "ones",
                axisTitle: "none",
                axisTitleText: "Value",
                labels: {
                    enabled: true
                },
                format: "number",
                formatOptions: {
                    decimal: 0,
                    separator: true,
                    abbreviated: false,
                    changeStyle: "none",
                    changeColor: true,
                    currency: "$",
                    dateFormat: "MMM Do",
                    accountingStyle: false
                }
            },
            yAxis2: {
                visible: false,
                opposite: true,
                showFirstLabel: true,
                showLastLabel: true,
                endOnTick: true,
                showAxisLine: true,
                showGridLines: true,
                showMajorTicks: false,
                showMinorTicks: false,
                labelAbbreviation: false,
                labelCurrency: "none",
                labelFormat: "ones",
                axisTitle: "none",
                axisTitleText: "Title",
                labels: {
                    enabled: true
                },
                format: "number",
                formatOptions: {
                    decimal: 0,
                    separator: true,
                    abbreviated: false,
                    changeStyle: "none",
                    changeColor: true,
                    currency: "$",
                    dateFormat: "MMM Do",
                    accountingStyle: false
                }
            },
            chartAnnotations: {
                items: [],
                connections: {
                    items: []
                }
            }
        };

        return {
            legendPosition: series.length > 1 ? "bottom" : "off",
            chartData: chartData
        };
    }

    buildStatisticChildElement(cell, builder) {
        return {
            value: builder.createSingleBlockText(cell.text || "TBD", "_use_styles_"),
            ornamentType: cell.statisticLabel ? "text" : "none",
            note: cell.statisticLabel ? builder.createSingleBlockText(cell.statisticLabel, "_use_styles_") : undefined,
            horizontalAlign: HorizontalAlignType.LEFT,
        };
    }

    buildRadialBarChildElement(cell, builder) {
        return {
            value: cell.value || 50,
            maxValue: 100,
            showValue: true,
            showLabel: cell.label != null,
            labelPosition: "inside",
            format: FormatType.PERCENT,
            color: "theme",
            label: cell.label ? builder.createSingleBlockText(cell.label, "_use_styles_") : undefined
        };
    }

    buildFactoidChildElement(cell, builder) {
        return {
            text: { blocks: cell.factoid }
        };
    }

    getDashboardLayout(cellCount) {
        const layouts = {
            1: {
                rows: 12,
                cols: 12,
                cells: [{
                    x: 0, y: 0, width: 12, height: 12
                }]
            },
            2: {
                rows: 12,
                cols: 12,
                cells: [{
                    x: 0, y: 0, width: 6, height: 12
                }, {
                    x: 6, y: 0, width: 6, height: 12
                }]
            },
            3: {
                rows: 12,
                cols: 12,
                cells: [{
                    x: 0, y: 0, width: 4, height: 12
                }, {
                    x: 4, y: 0, width: 4, height: 12
                }, {
                    x: 8, y: 0, width: 4, height: 12
                }]
            },
            4: {
                rows: 12,
                cols: 12,
                cells: [{
                    x: 0, y: 0, width: 6, height: 6
                }, {
                    x: 0, y: 6, width: 6, height: 6
                }, {
                    x: 6, y: 0, width: 6, height: 6
                }, {
                    x: 6, y: 6, width: 6, height: 6
                }]
            },
            5: {
                rows: 12,
                cols: 12,
                cells: [{
                    x: 0, y: 0, width: 4, height: 6
                }, {
                    x: 4, y: 0, width: 4, height: 6
                }, {
                    x: 8, y: 0, width: 4, height: 6
                }, {
                    x: 0, y: 6, width: 6, height: 6
                }, {
                    x: 6, y: 6, width: 6, height: 6
                }]
            },
            6: {
                rows: 12,
                cols: 12,
                cells: [{
                    x: 0, y: 0, width: 4, height: 6
                }, {
                    x: 4, y: 0, width: 4, height: 6
                }, {
                    x: 8, y: 0, width: 4, height: 6
                }, {
                    x: 0, y: 6, width: 4, height: 6
                }, {
                    x: 4, y: 6, width: 4, height: 6
                }, {
                    x: 8, y: 6, width: 4, height: 6
                }]
            },
            7: {
                rows: 12,
                cols: 12,
                cells: [{
                    x: 0, y: 0, width: 6, height: 12
                }, {
                    x: 6, y: 0, width: 2, height: 6
                }, {
                    x: 8, y: 0, width: 2, height: 6
                }, {
                    x: 10, y: 0, width: 2, height: 6
                }, {
                    x: 6, y: 6, width: 2, height: 6
                }, {
                    x: 8, y: 6, width: 2, height: 6
                }, {
                    x: 10, y: 6, width: 2, height: 6
                }]
            },
            8: {
                rows: 12,
                cols: 12,
                cells: [{
                    x: 0, y: 0, width: 3, height: 6
                }, {
                    x: 3, y: 0, width: 3, height: 6
                }, {
                    x: 6, y: 0, width: 3, height: 6
                }, {
                    x: 9, y: 0, width: 3, height: 6
                }, {
                    x: 0, y: 6, width: 3, height: 6
                }, {
                    x: 3, y: 6, width: 3, height: 6
                }, {
                    x: 6, y: 6, width: 3, height: 6
                }, {
                    x: 9, y: 6, width: 3, height: 6
                }]
            }
        };

        return layouts[cellCount] || layouts[1];
    }
}
